import { variantProvider } from '../theme'
import { includePresets } from '@codeleap/common'
import { ButtonComposition, CollapseComposition } from '@codeleap/web'
import { assignTextStyle } from './Text'

export type NavigationComposition =
  'section' |
  `collapse${Capitalize<CollapseComposition>}` |
  `title${Capitalize<ButtonComposition>}` |
  `item${Capitalize<ButtonComposition>}` |
  'titleWrapper:collapsible' |
  'titleText:collapsible' |
  `collapseWrapper:collapsible` |
  'itemWrapper:focused' |
  'itemText:focused' |
  'itemIcon:focused' |
  'group'

const createNavigationStyle = variantProvider.createVariantFactory<NavigationComposition>()

const presets = includePresets((s) => createNavigationStyle(() => ({ section: s })))

export const NavigationStyles = {
  ...presets,
  default: createNavigationStyle((theme) => ({
    section: {
      ...theme.presets.column,
    },
    'collapseWrapper': {
      ...theme.presets.column,
      ...theme.presets.fullWidth,
    },
    'titleWrapper': {
      backgroundColor: 'transparent',
      cursor: 'default',
      transition: 'all .2s ease',
      ...theme.spacing.marginBottom(1),

      '&:hover': {
        backgroundColor: theme.colors.neutral1,
      },
    },
    'titleWrapper:collapsible': {
      cursor: 'pointer',

      '&:hover': {
        background: theme.colors.neutral2,
      },
    },
    'titleIcon': {
      width: theme.values.iconSize[3],
      height: theme.values.iconSize[3],
    },
    'itemWrapper': {
      borderRadius: 0,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.neutral3,
      borderBottomStyle: 'solid',
      ...theme.spacing.padding(1.5),
      '&:hover': {
        backgroundColor: theme.colors.neutral3,
      },

      '&:last-of-type': {
        borderBottomWidth: 0,
      },
    },
    'itemText': {
      color: theme.colors.neutral9,
    },
  })),
  list: createNavigationStyle(theme => ({
    'section': {
      alignItems: 'flex-start',

      [theme.media.up('mid')]: {
        padding: theme.spacing.value(0),
        margin: theme.spacing.value(0),
      },

      [theme.media.down('mid')]: {
        ...theme.presets.fullWidth,
        flex: 1,
      },
    },
    'collapseWrapper': {
      borderRadius: theme.borderRadius.small,

      [theme.media.down('mid')]: {
        ...theme.presets.fullWidth,
        ...theme.presets.row,
      },
    },
    'group': {
      borderRadius: theme.borderRadius.small,
      overflow: 'hidden',
      ...theme.presets.column,

      [theme.media.down('mid')]: {
        ...theme.presets.fullWidth,
        ...theme.presets.row,

      },
    },
    'collapseWrapper:collapsible': {
      [theme.media.down('mid')]: {
        ...theme.presets.fullWidth,
        ...theme.presets.column,
      },
    },
    'titleText': {
      ...assignTextStyle('p2')(theme).text,
    },
    'itemWrapper': {
      backgroundColor: theme.colors.neutral2,
      borderRadius: 0,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.neutral3,
      borderBottomStyle: 'solid',
      ...theme.spacing.padding(2.5),
      textDecoration: 'none',

      '&:hover': {
        backgroundColor: theme.colors.neutral3,
      },

      [theme.media.down('mid')]: {
        flex: 1,
        borderWidth: 0,
      },
    },
    'itemWrapper:focused': {
      background: theme.colors.neutral3,

      '&:hover': {
        backgroundColor: theme.colors.neutral4,
      },
    },
  })),
  'lastItem:collapsible': createNavigationStyle(theme => ({
    'itemWrapper': {
      borderBottomRightRadius: theme.borderRadius.small,
      borderBottomLeftRadius: theme.borderRadius.small,
    },
  })),
  menu: createNavigationStyle(theme => ({
    'group': {
      overflow: 'hidden',
      ...theme.presets.column,
      ...theme.presets.fullWidth,
    },
    'itemWrapper': {
      backgroundColor: theme.colors.neutral1,
      borderRadius: 0,
      ...theme.border.neutral3({ 'directions': ['bottom'], 'width': 1 }),
      ...theme.spacing.paddingHorizontal(2),
      ...theme.spacing.paddingVertical(2),
      textDecoration: 'none',
      ...theme.spacing.gap(1),

      '&:hover': {
        backgroundColor: theme.colors.neutral1,
        filter: 'brightness(98%)',
      },

      '&:active': {
        backgroundColor: theme.colors.primary1,
      },

      [theme.media.down('mid')]: {
        flex: 1,
      },
    },
    'itemWrapper:focused': {
      [theme.media.down('mid')]: {
        flex: 1,
      },
    },
    'itemText': {
      ...theme.presets.fullWidth,
      textAlign: 'start',
      color: theme.colors.neutral6,
    },
    'itemText:focused': {
      color: theme.colors.primary3,
    },
    'itemIcon': {
      width: theme.values.iconSize[3],
      height: theme.values.iconSize[3],
      color: theme.colors.neutral7,
    },
    'itemIcon:focused': {
      color: theme.colors.primary3,
    },
  })),
}
